import { ConnectProto } from "@racoon/api";

const JOB_CATEGORY_TO_ROUTE_CATEGORY: Partial<
  Record<ConnectProto.JobCategory, ConnectProto.RouteCategory[]>
> = {
  [ConnectProto.JobCategory.CO]: [ConnectProto.RouteCategory.CO],
  [ConnectProto.JobCategory.MSW]: [ConnectProto.RouteCategory.MSW],
  [ConnectProto.JobCategory.DISINFECTION]: [ConnectProto.RouteCategory.DISINFECTION],
  [ConnectProto.JobCategory.LIQUID]: [ConnectProto.RouteCategory.LIQUID],
  [ConnectProto.JobCategory.TIPPER]: [ConnectProto.RouteCategory.TIPPER],
};

/**
 * The category a job of this type belongs to. `JobCreate` requires the category
 * explicitly, so anything that creates jobs from a job type alone — the pickup
 * location group's (container group, job type) links, for one — has to derive it.
 * Container operations (branding, repair, lids, tags) ride along with the
 * construction container they are performed on, hence CO.
 */
const JOB_TYPE_TO_JOB_CATEGORY: Partial<Record<ConnectProto.JobType, ConnectProto.JobCategory>> = {
  [ConnectProto.JobType.REPLACE_BUNKER]: ConnectProto.JobCategory.CO,
  [ConnectProto.JobType.ADD_BUNKER]: ConnectProto.JobCategory.CO,
  [ConnectProto.JobType.REMOVE_BUNKER]: ConnectProto.JobCategory.CO,
  [ConnectProto.JobType.RECEIVE_LOAD]: ConnectProto.JobCategory.CO,
  [ConnectProto.JobType.BRAND_CONTAINER]: ConnectProto.JobCategory.CO,
  [ConnectProto.JobType.REPAIR_CONTAINER]: ConnectProto.JobCategory.CO,
  [ConnectProto.JobType.DECOMMISSION_CONTAINER]: ConnectProto.JobCategory.CO,
  [ConnectProto.JobType.INSTALL_LID]: ConnectProto.JobCategory.CO,
  [ConnectProto.JobType.REMOVE_LID]: ConnectProto.JobCategory.CO,
  [ConnectProto.JobType.INSTALL_TAG]: ConnectProto.JobCategory.CO,
  [ConnectProto.JobType.MSW]: ConnectProto.JobCategory.MSW,
  [ConnectProto.JobType.MSW_CLEANUP]: ConnectProto.JobCategory.MSW,
  [ConnectProto.JobType.MSW_MOBILE_CLEANUP]: ConnectProto.JobCategory.MSW,
  [ConnectProto.JobType.DISINFECTION]: ConnectProto.JobCategory.DISINFECTION,
  [ConnectProto.JobType.LIQUID_WASTE]: ConnectProto.JobCategory.LIQUID,
  [ConnectProto.JobType.DOUBLE_CONSTRUCTION_REPLACE_BUNKER]: ConnectProto.JobCategory.TIPPER,
  [ConnectProto.JobType.DOUBLE_CONSTRUCTION_ADD_BUNKER]: ConnectProto.JobCategory.TIPPER,
  [ConnectProto.JobType.DOUBLE_CONSTRUCTION_REMOVE_BUNKER]: ConnectProto.JobCategory.TIPPER,
};

/** UNDEFINED for a job type that belongs to no category — the backend refuses it. */
export function getJobCategoryForJobType(
  jobType: ConnectProto.JobType | undefined,
): ConnectProto.JobCategory {
  if (jobType == null) return ConnectProto.JobCategory.UNDEFINED;

  return JOB_TYPE_TO_JOB_CATEGORY[jobType] ?? ConnectProto.JobCategory.UNDEFINED;
}

function isJobCategoryCompatibleWithRouteCategory(
  jobCategory: ConnectProto.JobCategory | undefined,
  routeCategory: ConnectProto.RouteCategory,
): boolean {
  if (!jobCategory) return false;

  const compatibleRouteCategories = JOB_CATEGORY_TO_ROUTE_CATEGORY[jobCategory];
  return compatibleRouteCategories?.includes(routeCategory) ?? false;
}

/**
 * The route categories a job of this category may join. Used when the jobs are
 * the known side and the route is the one being looked for.
 */
export function getRouteCategoriesForJobCategory(
  jobCategory: ConnectProto.JobCategory | undefined,
): ConnectProto.RouteCategory[] {
  if (!jobCategory) return [];

  return JOB_CATEGORY_TO_ROUTE_CATEGORY[jobCategory] ?? [];
}

/**
 * Which truck types may serve a route of this category — the vehicle-side twin
 * of the job table above, used to narrow "move to another vehicle" pickers and
 * the route list sidebars. A multilift (DOUBLE_CONTAINER) runs TIPPER routes,
 * as in `assign-route`.
 */
const ROUTE_CATEGORY_TO_TRUCK_TYPES: Partial<
  Record<ConnectProto.RouteCategory, ConnectProto.TruckType[]>
> = {
  [ConnectProto.RouteCategory.CO]: [ConnectProto.TruckType.CONSTRUCTION],
  [ConnectProto.RouteCategory.MSW]: [ConnectProto.TruckType.MSW],
  [ConnectProto.RouteCategory.LIQUID]: [ConnectProto.TruckType.LIQUID_WASTE],
  [ConnectProto.RouteCategory.TIPPER]: [
    ConnectProto.TruckType.TIPPER,
    ConnectProto.TruckType.DOUBLE_CONTAINER,
  ],
  [ConnectProto.RouteCategory.DISINFECTION]: [ConnectProto.TruckType.MSW],
};

/** An unset category on either side means "no constraint", so nothing is hidden. */
export function isTruckTypeCompatibleWithRouteCategory(
  truckType: ConnectProto.TruckType | undefined,
  routeCategory: ConnectProto.RouteCategory | undefined,
): boolean {
  if (routeCategory == null || routeCategory === ConnectProto.RouteCategory.UNDEFINED) return true;

  const truckTypes = ROUTE_CATEGORY_TO_TRUCK_TYPES[routeCategory];
  if (!truckTypes || truckTypes.length === 0) return true;
  if (truckType == null) return false;

  return truckTypes.includes(truckType);
}

export function areJobsCompatibleWithRouteCategory(
  jobs: Array<Pick<ConnectProto.Job, "jobCategory">>,
  routeCategory: ConnectProto.RouteCategory | undefined,
): boolean {
  if (jobs.length === 0) return true;
  // An unset category on either side carries no information, so treating it as
  // "incompatible" would hide every route on a data gap. The backend still
  // rejects a genuinely invalid move.
  if (routeCategory == null || routeCategory === ConnectProto.RouteCategory.UNDEFINED) return true;

  return jobs.every(
    (job) =>
      job.jobCategory == null ||
      job.jobCategory === ConnectProto.JobCategory.UNDEFINED ||
      isJobCategoryCompatibleWithRouteCategory(job.jobCategory, routeCategory),
  );
}

/**
 * A truck states its kind twice: on the custom truck type it was built from and,
 * for older records, directly on itself. The custom type wins where present.
 */
const getTruckTypeValue = (truck: ConnectProto.Truck | undefined) =>
  truck?.truckType?.type ?? truck?.type;

/**
 * Trucks that carry one job per trip: a construction hook-lift and a multilift
 * both haul a single container at a time, so a second job cannot share the trip
 * and belongs in a new one. The assign-route modal pushes every extra job into
 * a new trip; a pseudo-route, which is a single recorded trip, refuses it outright.
 */
export const isSingleJobPerTripTruckType = (truckType: ConnectProto.TruckType | undefined) =>
  truckType === ConnectProto.TruckType.CONSTRUCTION ||
  truckType === ConnectProto.TruckType.DOUBLE_CONTAINER;

/** The truck-object form: the custom truck type wins over the legacy field. */
export const isSingleJobPerTripTruck = (truck: ConnectProto.Truck | undefined): boolean =>
  isSingleJobPerTripTruckType(getTruckTypeValue(truck));

/**
 * The same rule asked of a route rather than a truck. The truck answers it
 * wherever the route carries one. A route whose shift did not load falls back to
 * its category, read through ROUTE_CATEGORY_TO_TRUCK_TYPES rather than named
 * here: a category counts as single-job only when *every* truck type serving it
 * is, so the fallback stays correct if that table gains a category or a type.
 */
export const isSingleJobPerTripRoute = (route: {
  routeCategory?: ConnectProto.RouteCategory;
  driverOnShift?: { truck?: ConnectProto.Truck };
}): boolean => {
  const truckType = getTruckTypeValue(route.driverOnShift?.truck);
  if (truckType != null && truckType !== ConnectProto.TruckType.UNDEFINED) {
    return isSingleJobPerTripTruckType(truckType);
  }

  if (route.routeCategory == null) return false;
  const truckTypes = ROUTE_CATEGORY_TO_TRUCK_TYPES[route.routeCategory];
  if (!truckTypes || truckTypes.length === 0) return false;

  return truckTypes.every(isSingleJobPerTripTruckType);
};

/**
 * Whether this truck can actually do this job — the job-first question the
 * vehicle pickers ask, as opposed to the route-first one above.
 *
 * The custom truck type's capability pairs decide when they are loaded: they have
 * to cover the job's (job type, container type), which is all the backend checks.
 * A самосвал with «Приём груза» takes that СО job although СО routes are a
 * бункеровоз's in the table above.
 *
 * Without them — `ListTruckDriverOnShifts` does not always embed the pairs — the
 * job's category has to belong to a route category the truck type serves, and an
 * unset category is permissive: hiding every vehicle on that gap would be worse
 * than letting the backend refuse on create.
 */
export function isJobServableByTruck(
  job: ConnectProto.Job | undefined,
  truck: ConnectProto.Truck | undefined,
): boolean {
  if (!job || !truck) return false;

  const capabilities = truck.truckType?.truckTypeCapabilities ?? [];
  if (capabilities.length > 0) {
    const containerTypeId = job.containerGroup?.containerType?.id;
    return capabilities.some(
      (capability) =>
        capability.jobType === job.type &&
        (!containerTypeId || String(capability.containerType?.id) === String(containerTypeId)),
    );
  }

  const truckType = getTruckTypeValue(truck);
  const routeCategories = getRouteCategoriesForJobCategory(job.jobCategory);
  return (
    routeCategories.length === 0 ||
    routeCategories.some((routeCategory) =>
      isTruckTypeCompatibleWithRouteCategory(truckType, routeCategory),
    )
  );
}

/** Every one of the jobs has to be servable — one that is not rules the truck out. */
export function areJobsServableByTruck(
  jobs: ConnectProto.Job[],
  truck: ConnectProto.Truck | undefined,
): boolean {
  if (jobs.length === 0) return true;

  return jobs.every((job) => isJobServableByTruck(job, truck));
}
